// GOLDEN PATH CODE
// This is an RFE Subpage Mesh Module based on best practices from
// AWT (https://awt.aws.dev/mesh/guide/introduction.html)
// It defines a sub-path for the Region Flex Portal, and loads up all
// relevent pages under that sub-path
import { defineModule, defineModuleMetadata } from "@amzn/awt-module-api";
import { defineViewTypes } from "@amzn/awt-module-views";
import { provideReactViewTypeHandler } from "@amzn/awt-module-views-react-18";
import {
  ContextLoader,
  NotFoundDoggyPage,
  RouteMap,
  websiteContributions,
} from "@amzn/region-flex-interface-module-website-common";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { SampleComponent } from "../components/SampleComponent";

// Golden Path Note:
// Modules should be named in the form @amzn/rfp{baseUrl}, where the
// sub-path slashes in the baseUrl are replaced with -
// This provides easy traceability for which modules are responsible
// for particular pages on the website
export const baseUrl = "/template";
export const moduleId = `@amzn/rfp${baseUrl.replaceAll("/", "-")}`;

// Golden Path Note:
// Modules should provide a RouteMap that is exposed to other
// modules (mostly the website module) via contribution points
// so that the website knows which routes are handled by the module
export const routes: RouteMap = {
  ["/"]: {
    title: () => "Mesh Modules Template", // Replace with page title
  },
};

export const viewTypes = defineViewTypes(moduleId, ({ withProps }) => ({
  routerView: withProps(),
}));

export const metadata = defineModuleMetadata(
  {
    moduleId,
    moduleVersion: "1.0.0",
  },
  // Golden Path Note:
  // These contributions enable the other modules to know exactly which routes the
  // module is responsible for, and allow for generation of various navigation elements
  ({ contribute }) => {
    if (websiteContributions.routeMap && websiteContributions.baseRoute) {
      contribute(websiteContributions.routeMap, [websiteContributions.routeMap.create(routes)]);
      contribute(websiteContributions.baseRoute, [
        websiteContributions.baseRoute.create({
          baseUrl,
          view: viewTypes.routerView,
        }),
      ]);
    }
  }
);

export const RoutesComponent = () => (
  <Routes>
    <Route path={"/"} element={<SampleComponent />} />
    {/* Render the catch-all route for undefined paths under the baseUrl */}
    <Route path="*" element={<NotFoundDoggyPage />} />
  </Routes>
);

// Golden Path Note:
// A sub-page module may contain several different pages in the RFP website
// As such, the base component for the sub-page module should be a browserRouter
// with a basename that matches the defined baseUrl.  This indicates to React Router
// that this module is responsible for tracking history and navigations to any
// pages that fall under the baseUrl
export const Router = () => (
  <BrowserRouter basename={baseUrl}>
    <ContextLoader>
      <RoutesComponent />
    </ContextLoader>
  </BrowserRouter>
);

export const module = defineModule({
  activate: (moduleContext) => {
    provideReactViewTypeHandler({
      moduleContext,
      viewTypeDefinition: viewTypes.routerView,
      componentProvider: async () => Router,
      on: {
        error: ({ error }) => console.error(error),
      },
    });
  },
});
